import { LOADING_THUMBNAIL_URL } from 'components/AsyncImage';
import { ProjectThumbnail } from 'components/Projects/styled';
import { getCustomAttribute } from 'helpers/mage-helpers/magento';
import { redirectToPage } from 'helpers/navigation';
import useProjects from 'hooks/useProjects';
import { DeviceSize, useWindowSize } from 'hooks/useWindowSize';
import React, { useMemo, useState, useEffect } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { customerCustomAttributesSelector, customerFirstNameSelector } from 'store/customer/selectors';
import { LoadingBar } from './LoadingBar';
import XSvg from '../../icons/X.svg';
import SvgContainer from 'icons/SvgContainer';
import itly from 'itly';
import {
  ReturnToProjectBackground,
  ReturnToProjectContainer,
  GreetingContainer,
  Greeting,
  GreetingSubtext,
  GoToProjectLink,
  GoToProjectsPageButton,
  DesktopProjectLoadingBarContainer,
  ProjectName,
  Description,
  LoadingBarContainer,
  ProjectThumbnailContainer,
  MobileProjectLoadingBarContainer,
  DismissReturnToProject,
  DismissReturnToProjectContainer
} from './styled';
import { ReturnToProjectData } from 'types/ReturnToProjectData';
import { clearReturnToProjectData, getReturnToProjectData } from 'helpers/projects/returnToProject';
import { transformToEllipsisText } from 'helpers/strings';

export interface ReturnToProjectProps {
  dismiss: () => void;
}

export const ReturnToProject = ({ dismiss }: ReturnToProjectProps): JSX.Element => {
  const { deviceSize } = useWindowSize();

  const customAttributes = useSelector(customerCustomAttributesSelector, shallowEqual);
  const flashId = getCustomAttribute('flash_id')(customAttributes);
  const flashToken = getCustomAttribute('flash_auth_token')(customAttributes);
  const firstName = useSelector(customerFirstNameSelector);

  const { getProjectThumbnailUrl } = useProjects(flashId, flashToken);

  const [returnToProjectData, setReturnToProjectData] = useState<ReturnToProjectData>(null);
  const { projectName, projectOptions, percentComplete, productName, projectUrl, sku } = returnToProjectData || {};

  const [thumbnailUrl, setThumbnailUrl] = useState(LOADING_THUMBNAIL_URL);

  const schemaVersion = useMemo(() => (projectUrl?.includes('/build') ? 2 : 1), [projectUrl]);

  const greeting = useMemo(
    () =>
      firstName?.length ? `Welcome${deviceSize === DeviceSize.SMALL ? '' : ' back'}, ${firstName}` : 'Welcome back!',
    [firstName, deviceSize]
  );

  useEffect(() => {
    const returnToProjectData = getReturnToProjectData();
    setReturnToProjectData(returnToProjectData);
  }, []);

  useEffect(() => {
    (async () => {
      if (returnToProjectData) {
        const thumbnailUrl = await getProjectThumbnailUrl(sku, projectOptions, schemaVersion);
        setThumbnailUrl(thumbnailUrl);
      }
    })();
  // TODO: Refactor this dependency
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sku, projectOptions, schemaVersion]);

  const handleDismissReturnToProject = () => {
    itly.returnToProjectDismissed();
    clearReturnToProjectData();
    dismiss();
  };

  useEffect(() => {
    itly.returnToProjectImpression();
  }, []);

  const isMobile = deviceSize === DeviceSize.SMALL;

  const formatProjectName = transformToEllipsisText(projectName, 18, isMobile);

  return (
    returnToProjectData && (
      <ReturnToProjectBackground>
        <ReturnToProjectContainer>
          <DismissReturnToProjectContainer>
            <DismissReturnToProject onClick={handleDismissReturnToProject}>{SvgContainer(XSvg)}</DismissReturnToProject>
          </DismissReturnToProjectContainer>
          <GreetingContainer>
            <Greeting>{greeting}</Greeting>
            <GreetingSubtext>
              Continue where you left off on your <br /> {productName}
            </GreetingSubtext>
            <GoToProjectLink
              href={projectUrl}
              onClick={() => {
                itly.returnToProjectClicked({ click_type: 'Continue' });
              }}
            >
              Continue Designing
            </GoToProjectLink>
            <GoToProjectsPageButton
              onClick={() => {
                itly.returnToProjectClicked({ click_type: 'All projects' });
                redirectToPage('/account/projects/');
              }}
            >
              View All Projects
            </GoToProjectsPageButton>
            <MobileProjectLoadingBarContainer>
              <Description>{percentComplete}% - Complete </Description>
              <LoadingBarContainer>
                <LoadingBar percentComplete={percentComplete} />
              </LoadingBarContainer>
            </MobileProjectLoadingBarContainer>
          </GreetingContainer>
          <ProjectThumbnailContainer>
            <ProjectThumbnail
              key="return-to-project"
              url={thumbnailUrl}
              isLoading={thumbnailUrl === LOADING_THUMBNAIL_URL}
            >
              <DesktopProjectLoadingBarContainer>
                <ProjectName title={projectName}>{formatProjectName}</ProjectName>
                <Description>{percentComplete}% - Complete </Description>
                <LoadingBarContainer>
                  <LoadingBar percentComplete={percentComplete} />
                </LoadingBarContainer>
              </DesktopProjectLoadingBarContainer>
            </ProjectThumbnail>
          </ProjectThumbnailContainer>
        </ReturnToProjectContainer>
      </ReturnToProjectBackground>
    )
  );
};
